import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import {
  MessageCircle,
  Send,
  CalendarClock,
  Users,
  Trash2,
  AlertCircle,
  RefreshCw,
  Paperclip,
  History,
  Sparkles,
  Phone,
  FileSpreadsheet,
  Download,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { clientsAPI, whatsappAPI } from '../services/api';
import {
  ModernButton,
  ModernCard,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import { normalizePhoneDigits, phoneSearchMatches } from '../utils/phoneNormalize';
import {
  parsePhoneNumbersFromXlsxArrayBuffer,
  downloadWhatsappNumbersTemplateXlsx
} from '../utils/whatsappExcelPhones';

const TZ_LABEL = 'Fuseau serveur : Africa/Casablanca';

/** react-select : recherche nom ou numéro (+212, 06, 212… équivalents) */
function filterClientOption(option, inputValue, clients) {
  const input = (inputValue || '').trim();
  if (!input) return true;
  const client = clients.find((c) => c.id === option.value);
  if (!client) return false;
  const name = (client.name || '').toLowerCase();
  if (name.includes(input.toLowerCase())) return true;
  if (phoneSearchMatches(client.phone, input)) return true;
  return false;
}

function formatScheduledAt(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('fr-FR', { timeZone: 'Africa/Casablanca' });
  } catch {
    return String(v);
  }
}

function parseClientIds(json) {
  if (json == null) return [];
  if (Array.isArray(json)) return json;
  try {
    const p = typeof json === 'string' ? JSON.parse(json) : json;
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

const statusBadge = {
  pending: 'bg-amber-100 text-amber-900 border-amber-200',
  sent: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  failed: 'bg-rose-100 text-rose-900 border-rose-200',
  cancelled: 'bg-neutral-200 text-neutral-700 border-neutral-300'
};

const statusLabel = {
  pending: 'En attente',
  sent: 'Envoyé',
  failed: 'Échec',
  cancelled: 'Annulé'
};

const instantStatusBadge = {
  sent: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  partial: 'bg-amber-100 text-amber-900 border-amber-200',
  failed: 'bg-rose-100 text-rose-900 border-rose-200'
};

const instantStatusLabel = {
  sent: 'OK',
  partial: 'Partiel',
  failed: 'Échec'
};

/** Modèles pour opticien — magasin Epoptique ; adaptez dates / heures / jour si besoin */
const MESSAGE_TEMPLATES_OPTIC = [
  {
    id: 'confirm',
    label: 'Confirmation commande',
    body: `Bonjour,

Votre commande a bien été enregistrée. Nous vous préviendrons dès qu'elle sera prête au magasin.

Merci de votre confiance.
Epoptique`
  },
  {
    id: 'cancel',
    label: 'Annulation commande',
    body: `Bonjour,

Votre commande a été annulée, conformément à votre demande.

Pour toute question, vous pouvez nous contacter au magasin.

Cordialement,
Epoptique`
  },
  {
    id: 'progress',
    label: 'Commande en cours',
    body: `Bonjour,

Votre commande est en cours de préparation (monture / verres). Nous vous tiendrons informé(e) dès qu'elle sera disponible.

Merci de votre patience.
Epoptique`
  },
  {
    id: 'delivered',
    label: 'Commande prête / livrée',
    body: `Bonjour,

Bonne nouvelle : votre commande est prête. Vous pouvez passer la récupérer au magasin aux horaires d'ouverture.

N'hésitez pas à nous appeler avant votre passage si besoin.

À bientôt !
Epoptique`
  },
  {
    id: 'promo',
    label: 'Promotion',
    body: `🎉 Offre spéciale chez Epoptique !

Profitez de nos promotions sur une sélection de montures et de verres. Demandez-nous les conditions en magasin ou par message.

[Dates ou durée de l'offre]

À très vite !
Epoptique`
  },
  {
    id: 'newstock',
    label: 'Nouvelle arrivage',
    body: `👓 Nouvelles collections disponibles !

De nouveaux modèles de lunettes et solaires viennent d'arriver. Passez au magasin pour les essayer : notre équipe vous conseille avec plaisir.

Epoptique`
  },
  {
    id: 'pickup',
    label: 'Rappel retrait commande',
    body: `Bonjour,

Votre monture / votre équipement est prêt au magasin. Pensez à passer le récupérer quand cela vous convient.

Merci et à bientôt !
Epoptique`
  },
  {
    id: 'rdv',
    label: 'Rappel rendez-vous',
    body: `Bonjour,

Petit rappel : votre rendez-vous à l'atelier / pour un contrôle visuel est prévu le [jour] à [heure].

En cas d'empêchement, merci de nous prévenir pour reprogrammer.

À bientôt,
Epoptique`
  },
  {
    id: 'thanks',
    label: 'Remerciement / suivi',
    body: `Bonjour,

Merci pour votre visite au magasin. N'hésitez pas à nous recontacter si vous avez la moindre question sur vos lunettes ou lentilles.

Excellente journée !
Epoptique`
  }
];

export default function WhatsApp() {
  const SENT_PAGE_SIZE = 10;
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [manualPhone, setManualPhone] = useState('');
  const [excelImportedPhones, setExcelImportedPhones] = useState([]);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);
  const [sending, setSending] = useState(false);

  const [scheduledList, setScheduledList] = useState([]);
  const [loadingSched, setLoadingSched] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [showScheduleFields, setShowScheduleFields] = useState(false);

  const [sentLogList, setSentLogList] = useState([]);
  const [loadingSent, setLoadingSent] = useState(true);
  const [sentPage, setSentPage] = useState(1);
  const [templateOverrides, setTemplateOverrides] = useState({});
  const [openManualPhone, setOpenManualPhone] = useState(false);
  const [openExcelImport, setOpenExcelImport] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await clientsAPI.getClients();
      setClients(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Impossible de charger les clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadScheduled = useCallback(async () => {
    setLoadingSched(true);
    try {
      const { data } = await whatsappAPI.listScheduled();
      setScheduledList(Array.isArray(data) ? data : []);
    } catch {
      setScheduledList([]);
    } finally {
      setLoadingSched(false);
    }
  }, []);

  const loadWhatsAppConfig = useCallback(async () => {
    try {
      const { data } = await whatsappAPI.getConfig();
      setTemplateOverrides({
        confirm: data?.message_template_confirmation || '',
        cancel: data?.message_template_annulation || '',
        progress: data?.message_template_en_cours || '',
        delivered: data?.message_template_livree || '',
        promo: data?.message_template_promotion || '',
        newstock: data?.message_template_nouvel_arrivage || '',
      });
    } catch {
      // optional: keep defaults
    }
  }, []);

  const loadSentInstant = useCallback(async () => {
    setLoadingSent(true);
    try {
      const { data } = await whatsappAPI.listSentInstant();
      setSentLogList(Array.isArray(data) ? data : []);
    } catch {
      setSentLogList([]);
    } finally {
      setLoadingSent(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadScheduled();
    loadSentInstant();
    loadWhatsAppConfig();
  }, [loadClients, loadScheduled, loadSentInstant, loadWhatsAppConfig]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') loadScheduled();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadScheduled]);

  /** Rafraîchir la liste tant qu’il reste des envois « En attente » (statut mis à jour côté serveur après l’heure prévue). */
  useEffect(() => {
    const hasPending = scheduledList.some((r) => r.status === 'pending');
    if (!hasPending) return undefined;
    const t = setInterval(() => loadScheduled(), 12000);
    return () => clearInterval(t);
  }, [scheduledList, loadScheduled]);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => {
          const n = normalizePhoneDigits(c.phone);
          return n.length >= 10;
        })
        .map((c) => ({
          value: c.id,
          label: `${c.name} — ${c.phone || ''}`
        })),
    [clients]
  );
  const allClientsSelected = clientOptions.length > 0 && selected.length === clientOptions.length;

  const messageTemplatesOptic = useMemo(
    () =>
      MESSAGE_TEMPLATES_OPTIC.map((t) => ({
        ...t,
        body: (templateOverrides[t.id] || t.body || '').trim() || t.body,
      })),
    [templateOverrides]
  );

  const sentTotalPages = Math.max(1, Math.ceil(sentLogList.length / SENT_PAGE_SIZE));
  const sentPageRows = useMemo(
    () => sentLogList.slice((sentPage - 1) * SENT_PAGE_SIZE, sentPage * SENT_PAGE_SIZE),
    [sentLogList, sentPage, SENT_PAGE_SIZE]
  );

  useEffect(() => {
    if (sentPage > sentTotalPages) setSentPage(sentTotalPages);
  }, [sentPage, sentTotalPages]);

  const clearAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /** Fusionne saisie manuelle + import Excel, dédoublonnage par numéro normalisé. */
  const buildPhoneNumbersPayload = useCallback(() => {
    const seen = new Set();
    const out = [];
    const push = (raw) => {
      const t = String(raw).trim();
      if (!t) return;
      const n = normalizePhoneDigits(t);
      if (n.length < 10) return;
      if (seen.has(n)) return;
      seen.add(n);
      out.push(t);
    };
    push(manualPhone);
    excelImportedPhones.forEach(push);
    return out.length ? out : undefined;
  }, [manualPhone, excelImportedPhones]);

  const clearExcelImport = () => {
    setExcelImportedPhones([]);
    if (excelInputRef.current) excelInputRef.current.value = '';
  };

  const handleDownloadExcelTemplate = async () => {
    try {
      await downloadWhatsappNumbersTemplateXlsx();
      toast.success('Modèle téléchargé');
    } catch (e) {
      console.error(e);
      toast.error('Impossible de générer le modèle');
    }
  };

  const handleExcelImport = async (e) => {
    const input = e.target;
    const f = input.files?.[0];
    if (!f) return;
    const okName = /\.xlsx$/i.test(f.name);
    const okType =
      !f.type ||
      f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (!okName && !okType) {
      toast.error('Utilisez un fichier .xlsx (Excel ou Google Sheets : Fichier → Télécharger → Microsoft Excel)');
      input.value = '';
      return;
    }
    try {
      const buf = await f.arrayBuffer();
      const { phones, skippedRows, sheetName } = await parsePhoneNumbersFromXlsxArrayBuffer(buf);
      if (!phones.length) {
        toast.error(
          'Aucun numéro valide (10+ chiffres). Utilisez une colonne « téléphone » en ligne 1, ou placez un numéro par ligne.',
          { duration: 6000 }
        );
        input.value = '';
        return;
      }
      setExcelImportedPhones(phones);
      let msg = `${phones.length} numéro(s) importé(s)`;
      if (skippedRows) msg += ` — ${skippedRows} ligne(s) ignorée(s)`;
      if (sheetName) msg += ` — feuille « ${sheetName} »`;
      if (phones.length >= 80) {
        msg += '. L’envoi peut prendre plusieurs minutes (espacement API).';
      }
      toast.success(msg, { duration: 6000 });
    } catch (err) {
      console.error(err);
      toast.error(
        'Lecture Excel impossible. Ré-enregistrez le fichier en .xlsx depuis Excel ou Google Sheets.',
        { duration: 6000 }
      );
      input.value = '';
    }
  };

  const handleSendNow = async () => {
    const phonePayload = buildPhoneNumbersPayload();
    if (!selected.length && !phonePayload?.length) {
      toast.error('Sélectionnez des clients, un numéro, ou importez un fichier Excel');
      return;
    }
    const text = message.trim();
    if (!attachment && !text) {
      toast.error('Saisissez un message ou choisissez un fichier (image ou PDF)');
      return;
    }
    setSending(true);
    try {
      let data;
      if (attachment) {
        const fd = new FormData();
        fd.append('clientIds', JSON.stringify(selected.map((o) => o.value)));
        if (phonePayload) fd.append('phoneNumbers', JSON.stringify(phonePayload));
        fd.append('message', text);
        fd.append('file', attachment);
        ({ data } = await whatsappAPI.send(fd));
      } else {
        ({ data } = await whatsappAPI.send({
          clientIds: selected.map((o) => o.value),
          ...(phonePayload ? { phoneNumbers: phonePayload } : {}),
          message: text
        }));
      }
      const results = data.results || [];
      const ok = results.filter((r) => r.ok).length;
      const fail = results.length - ok;
      const firstErr = results.find((r) => !r.ok)?.error;
      if (results.length > 0 && ok === 0) {
        toast.error(
          firstErr ||
            'Aucun envoi réussi. Vérifiez Green API (instance, GREEN_API_MEDIA_URL dans .env), et le numéro WhatsApp.',
          { duration: 8000 }
        );
      } else if (fail > 0) {
        toast.error(`Échec partiel : ${ok} ok, ${fail} erreur(s). ${firstErr || ''}`, { duration: 8000 });
      } else {
        toast.success(`Message envoyé à ${ok} destinataire(s)`);
        setMessage('');
        setManualPhone('');
        clearExcelImport();
        clearAttachment();
        setShowScheduleFields(false);
      }
      loadSentInstant();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur envoi WhatsApp');
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    const phonePayload = buildPhoneNumbersPayload();
    if (!selected.length && !phonePayload?.length) {
      toast.error('Sélectionnez des clients, un numéro, ou importez un fichier Excel');
      return;
    }
    const text = message.trim();
    if (!attachment && !text) {
      toast.error('Saisissez un message ou choisissez un fichier (image ou PDF)');
      return;
    }
    if (!scheduledAt) {
      toast.error('Choisissez une date et heure');
      return;
    }
    const when = new Date(scheduledAt);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error('La date doit être dans le futur');
      return;
    }
    setScheduling(true);
    try {
      if (attachment) {
        const fd = new FormData();
        fd.append('clientIds', JSON.stringify(selected.map((o) => o.value)));
        if (phonePayload) fd.append('phoneNumbers', JSON.stringify(phonePayload));
        fd.append('message', text);
        fd.append('scheduledAt', when.toISOString());
        fd.append('file', attachment);
        await whatsappAPI.schedule(fd);
      } else {
        await whatsappAPI.schedule({
          clientIds: selected.map((o) => o.value),
          ...(phonePayload ? { phoneNumbers: phonePayload } : {}),
          message: text,
          scheduledAt: when.toISOString()
        });
      }
      toast.success('Envoi planifié');
      setSelected([]);
      setManualPhone('');
      clearExcelImport();
      setMessage('');
      clearAttachment();
      setScheduledAt('');
      setShowScheduleFields(false);
      loadScheduled();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur planification');
    } finally {
      setScheduling(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cet envoi planifié ?')) return;
    try {
      await whatsappAPI.cancelScheduled(id);
      toast.success('Planification annulée');
      loadScheduled();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <MessageCircle className="h-8 w-8 text-emerald-600" />
          WhatsApp (Green API)
        </h1>
        <p className="text-neutral-600 mt-1">
          Envoyez un message texte, une image ou un PDF aux clients du magasin. Les numéros doivent être valides (ex.
          mobile Maroc).
        </p>
        <p className="text-xs text-neutral-500 mt-1">{TZ_LABEL}</p>
      </div>

      <ModernCard className="p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600" />
          Destinataires
        </h2>
        <label className="block text-sm font-medium text-neutral-700 mb-2">Clients (multi-sélection)</label>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSelected(clientOptions)}
            disabled={!clientOptions.length || allClientsSelected}
            className="inline-flex items-center rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            Tout sélectionner
          </button>
          <button
            type="button"
            onClick={() => setSelected([])}
            disabled={!selected.length}
            className="inline-flex items-center rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            Effacer la sélection
          </button>
        </div>
        <Select
          isMulti
          options={clientOptions}
          value={selected}
          onChange={setSelected}
          placeholder="Nom, +212…, 06…, 212…"
          classNamePrefix="react-select"
          filterOption={(opt, input) => filterClientOption(opt, input, clients)}
          noOptionsMessage={() => 'Aucun client avec téléphone valide'}
        />
        <p className="mt-2 text-xs text-neutral-500">
          Recherche : le même numéro est reconnu en +212, 212, 06… ou sans indicatif.
        </p>

        <div className="mt-5 rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenManualPhone((v) => !v)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            aria-expanded={openManualPhone}
          >
            {openManualPhone ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            )}
            <Phone className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            <span className="flex-1 min-w-0">Numéro libre (hors liste clients)</span>
            {!openManualPhone && manualPhone.trim() ? (
              <span className="text-xs font-normal text-emerald-700 shrink-0">Numéro saisi</span>
            ) : null}
          </button>
          {openManualPhone ? (
            <div className="border-t border-neutral-100 px-4 pb-4 pt-2 space-y-2">
              <input
                type="text"
                className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                placeholder="Ex. +212 611 159 538 ou 0611159538"
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                autoComplete="tel"
              />
              <p className="text-xs text-neutral-500">
                Optionnel — envoyez au numéro saisi seul ou en plus des clients sélectionnés (doublons ignorés). Même
                format que pour les fiches client.
              </p>
            </div>
          ) : null}
        </div>

        <div className="mt-3 rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenExcelImport((v) => !v)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            aria-expanded={openExcelImport}
          >
            {openExcelImport ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            )}
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
            <span className="flex-1 min-w-0">Importer des numéros (.xlsx)</span>
            {!openExcelImport && excelImportedPhones.length > 0 ? (
              <span className="text-xs font-normal text-emerald-700 shrink-0">
                {excelImportedPhones.length} numéro{excelImportedPhones.length > 1 ? 's' : ''}
              </span>
            ) : null}
          </button>
          {openExcelImport ? (
            <div className="border-t border-neutral-100 px-4 pb-4 pt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="block w-full max-w-md text-sm text-neutral-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-800 hover:file:bg-emerald-100"
                  onChange={handleExcelImport}
                />
                <button
                  type="button"
                  onClick={handleDownloadExcelTemplate}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Télécharger un modèle (.xlsx)
                </button>
                {excelImportedPhones.length > 0 && (
                  <button
                    type="button"
                    onClick={clearExcelImport}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Vider l’import ({excelImportedPhones.length})
                  </button>
                )}
              </div>
              <p className="text-xs text-neutral-500 leading-relaxed">
                <span className="font-medium text-neutral-600">Première feuille</span> du classeur : en ligne 1, une
                colonne intitulée <code className="text-neutral-700 bg-neutral-100 px-1 rounded">telephone</code>,{' '}
                <code className="text-neutral-700 bg-neutral-100 px-1 rounded">téléphone</code>,{' '}
                <code className="text-neutral-700 bg-neutral-100 px-1 rounded">mobile</code> ou{' '}
                <code className="text-neutral-700 bg-neutral-100 px-1 rounded">whatsapp</code> ; les numéros suivent en
                dessous. Sans en-tête reconnu, la première cellule de chaque ligne contenant un numéro valide est
                utilisée.
              </p>
              <p className="text-xs text-neutral-500 leading-relaxed">
                <span className="font-medium text-neutral-600">Canva</span> ne propose pas d’export Excel de contacts :
                listez vos numéros dans <strong>Google Sheets</strong> ou <strong>Excel</strong>, puis Fichier →
                Télécharger → <strong>.xlsx</strong> (ou copiez-collez la colonne dans le modèle ci-dessus).
              </p>
            </div>
          ) : null}
        </div>

        {clients.length > 0 && clientOptions.length === 0 && (
          <p className="mt-2 text-sm text-amber-700 flex items-center gap-1">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Aucun téléphone utilisable. Renseignez les numéros dans la fiche client.
          </p>
        )}
      </ModernCard>

      <ModernCard className="p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Message</h2>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-stretch">
          <div className="flex-1 min-w-0 flex flex-col">
            <textarea
              className="w-full flex-1 min-h-[220px] lg:min-h-[320px] rounded-xl border border-neutral-200 px-4 py-3 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
              placeholder="Légende ou message texte (optionnel si vous joignez un fichier)…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={10000}
            />
            <p className="text-xs text-neutral-500 mt-1">{message.length} / 10000 caractères</p>

            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2 flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-neutral-500" />
                Pièce jointe (JPEG, PNG, PDF, HEIC… max 16 Mo). Le fichier est envoyé au stockage Green puis distribué
                sur WhatsApp.
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,application/pdf,.pdf,.heic,.heif"
                className="block w-full text-sm text-neutral-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setAttachment(f || null);
                }}
              />
              {attachment && (
                <p className="mt-2 text-xs text-neutral-600 flex items-center gap-2">
                  <span className="truncate font-medium">{attachment.name}</span>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    className="text-rose-600 hover:underline shrink-0"
                  >
                    Retirer
                  </button>
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ModernButton
                variant="primary"
                icon={<Send className="h-4 w-4" />}
                onClick={handleSendNow}
                disabled={sending || scheduling}
              >
                {sending ? 'Envoi…' : 'Envoyer maintenant'}
              </ModernButton>
              <ModernButton
                variant="secondary"
                icon={<CalendarClock className="h-4 w-4" />}
                onClick={() => setShowScheduleFields((v) => !v)}
                disabled={sending || scheduling}
              >
                {showScheduleFields ? 'Masquer planification' : 'Planifier'}
              </ModernButton>
            </div>

            {showScheduleFields && (
              <div className="mt-4 rounded-2xl border border-neutral-200 bg-gradient-to-b from-white to-neutral-50 p-4 space-y-3 shadow-sm">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Date et heure</label>
                  <input
                    type="datetime-local"
                    className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                </div>
                <ModernButton
                  variant="primary"
                  icon={<CalendarClock className="h-4 w-4" />}
                  onClick={handleSchedule}
                  disabled={sending || scheduling}
                >
                  {scheduling ? 'Planification…' : 'Confirmer planification'}
                </ModernButton>
              </div>
            )}
          </div>

          <aside className="w-full shrink-0 border-t xl:border-t-0 xl:border-l border-neutral-200 pt-4 xl:pt-0 xl:pl-6">
            <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
              Exemples (optique)
            </h3>
            <p className="text-xs text-neutral-500 mb-3">
              Cliquez pour insérer dans le message à gauche — adaptez dates, prénom ou détails si besoin.
            </p>
            <div className="flex flex-col gap-2 max-h-[min(480px,55vh)] overflow-y-auto pr-1 -mr-1">
              {messageTemplatesOptic.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setMessage(t.body);
                    toast.success('Modèle inséré — vous pouvez le modifier avant envoi.');
                  }}
                  className="text-left rounded-lg border border-neutral-200 bg-neutral-50 hover:bg-emerald-50 hover:border-emerald-200 px-3 py-2.5 text-sm transition-colors"
                >
                  <span className="font-semibold text-neutral-900 block text-sm">{t.label}</span>
                  <span className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{t.body.split('\n')[0]}…</span>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </ModernCard>

      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
            <History className="h-5 w-5 text-teal-600" />
            Historique — envois immédiats
          </h2>
          <button
            type="button"
            onClick={() => {
              setSentPage(1);
              loadSentInstant();
            }}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
          >
            <RefreshCw className={`h-4 w-4 ${loadingSent ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Chaque clic sur « Envoyer maintenant » est enregistré (message, destinataires, résultat Green API).
        </p>
        {loadingSent && sentLogList.length === 0 ? (
          <ModernLoadingSpinner />
        ) : sentLogList.length === 0 ? (
          <p className="text-sm text-neutral-500">Aucun envoi immédiat enregistré pour l’instant.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 text-left text-neutral-600">
                    <th className="pb-2 pr-2">Date / heure</th>
                    <th className="pb-2 pr-2">Résultat</th>
                    <th className="pb-2 pr-2">Clients (ids)</th>
                    <th className="pb-2 pr-2">Message</th>
                    <th className="pb-2 pr-2">Fichier</th>
                    <th className="pb-2 pr-2">Par</th>
                  </tr>
                </thead>
                <tbody>
                  {sentPageRows.map((row) => (
                    <tr key={row.id} className="border-b border-neutral-100">
                      <td className="py-2 pr-2 whitespace-nowrap">{formatScheduledAt(row.sent_at)}</td>
                      <td className="py-2 pr-2">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold border ${
                            instantStatusBadge[row.status] || instantStatusBadge.failed
                          }`}
                        >
                          {instantStatusLabel[row.status] || row.status}
                        </span>
                      </td>
                      <td className="py-2 pr-2 max-w-[120px] truncate" title={JSON.stringify(parseClientIds(row.client_ids))}>
                        {parseClientIds(row.client_ids).join(', ')}
                      </td>
                      <td className="py-2 pr-2 max-w-[200px] truncate" title={row.message}>
                        {row.message || (row.attachment_filename ? '—' : '')}
                      </td>
                      <td className="py-2 pr-2 whitespace-nowrap text-neutral-600">
                        {row.attachment_filename ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <Paperclip className="h-3.5 w-3.5" />
                            Oui
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 pr-2 max-w-[140px] truncate text-neutral-600" title={row.creator_name}>
                        {row.creator_name || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sentTotalPages > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-500">
                {(sentPage - 1) * SENT_PAGE_SIZE + 1}–{Math.min(sentPage * SENT_PAGE_SIZE, sentLogList.length)} sur {sentLogList.length}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={sentPage === 1}
                  onClick={() => setSentPage((p) => Math.max(1, p - 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-white text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:pointer-events-none"
                >
                  ‹
                </button>
                {Array.from({ length: sentTotalPages }, (_, i) => i + 1)
                  .slice(Math.max(0, sentPage - 3), Math.max(0, sentPage - 3) + 5)
                  .map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setSentPage(page)}
                      className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg border px-2 text-sm ${
                        page === sentPage
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                <button
                  type="button"
                  disabled={sentPage === sentTotalPages}
                  onClick={() => setSentPage((p) => Math.min(sentTotalPages, p + 1))}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 bg-white text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-40 disabled:pointer-events-none"
                >
                  ›
                </button>
              </div>
            </div>
            )}
          </>
        )}
      </ModernCard>

      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900">Envois planifiés</h2>
          <button
            type="button"
            onClick={loadScheduled}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800"
          >
            <RefreshCw className={`h-4 w-4 ${loadingSched ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
        {loadingSched && scheduledList.length === 0 ? (
          <ModernLoadingSpinner />
        ) : scheduledList.length === 0 ? (
          <p className="text-sm text-neutral-500">Aucun envoi planifié.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-neutral-600">
                  <th className="pb-2 pr-2">Date / heure</th>
                  <th className="pb-2 pr-2">Statut</th>
                  <th className="pb-2 pr-2">Clients (ids)</th>
                  <th className="pb-2 pr-2">Message</th>
                  <th className="pb-2 pr-2">Fichier</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {scheduledList.map((row) => (
                  <tr key={row.id} className="border-b border-neutral-100">
                    <td className="py-2 pr-2 whitespace-nowrap">{formatScheduledAt(row.scheduled_at)}</td>
                    <td className="py-2 pr-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold border ${
                          statusBadge[row.status] || statusBadge.pending
                        }`}
                      >
                        {statusLabel[row.status] || row.status}
                      </span>
                    </td>
                    <td className="py-2 pr-2 max-w-[120px] truncate" title={JSON.stringify(parseClientIds(row.client_ids))}>
                      {parseClientIds(row.client_ids).join(', ')}
                    </td>
                    <td className="py-2 pr-2 max-w-[200px] truncate" title={row.message}>
                      {row.message || (row.attachment_filename ? '—' : '')}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap text-neutral-600">
                      {row.attachment_filename ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Paperclip className="h-3.5 w-3.5" />
                          Oui
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {row.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleCancel(row.id)}
                          className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50"
                          title="Annuler"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ModernCard>
    </div>
  );
}
